import {
    COLOR_06,
    COLOR_10,
    COLOR_25,
    COLOR_26,
    COLOR_29,
    COLOR_30,
    COLOR_31,
    COLOR_32, 
    COLOR_33,
    COLOR_35,
    COLOR_36
} from "../../../constants";
import {
    ThemeName
} from "../../../types";
import {
    interpolateColors
} from "../../../utils";

enum DonutThemes {
    altBlue = 'altBlue',
    altBlue2 = 'altBlue2',
    altGreen = 'altGreen',
    altGreen2 = 'altGreen2'
}

type DonutThemeName = ThemeName | keyof typeof DonutThemes;

type DonutColorSet = [
    firstColor: string,
    secondColor: string,
    backgroundColor: string
];

type DonutColorSets = {
    [themeName in DonutThemeName]: DonutColorSet
};

type DonutValueSet = {
    values: DonutValue[],
    maxValue?: number,
    theme?: DonutThemeName
}

type DonutValue = number | {
    value: number,
    tooltip?: string | boolean
};

export type DonutValueSetProp = (DonutValueSet | undefined) | (DonutValueSet | undefined)[];

const donutColorSets: DonutColorSets = {
    'blue': [COLOR_25, COLOR_26, COLOR_30],
    'altBlue': [COLOR_10, COLOR_10, COLOR_06],
    'altBlue2': [COLOR_29, COLOR_29, COLOR_06],
    'green': [COLOR_35, COLOR_36, COLOR_33],
    'altGreen': [COLOR_31, COLOR_31, COLOR_33],
    'altGreen2': [COLOR_32, COLOR_32, COLOR_33]
};

const defaultArcWidth = 25;
const reductionConstant = 8;
const reductionConstantNoOverlay = -8;
const separation = 5;

const fallbackColorSet: DonutColorSet = ['#808080', '#cbcbcb', 'transparent'];

export const getValuesArray = (
    donutValues: DonutValue[]
): number[] => donutValues.map(donutValue => (
    typeof donutValue === 'number'
        ? donutValue
        : donutValue.value
));

export const getBackgroundColors = (
    themeName: DonutThemeName | undefined,
    dataLength: number
): string[] => {
    const colorSet = themeName ? donutColorSets[themeName] : fallbackColorSet;

    if (dataLength === 0) {
        return [colorSet[2]];
    }

    if (dataLength === 1) {
        return [colorSet[0], colorSet[2]];
    }

    if (dataLength === 2) {
        return colorSet;
    }
    
    return [
        ...(interpolateColors(colorSet[0], colorSet[1], dataLength)?.map(String) || [colorSet[0], colorSet[1]]),
        colorSet[2]
    ];
};

export const getRadiusRange = (
    valueSetIndex: number,
    valueSetsLength: number,
    hasOverlay: boolean = true
): [string, string] => {
    const widthReduction = 0 + ((valueSetsLength - 1) * (hasOverlay ? reductionConstant : reductionConstantNoOverlay));
    const arcWidth = defaultArcWidth - widthReduction;

    const startRadius = 100 - (valueSetIndex * (arcWidth + separation));
    const endRadius = startRadius - arcWidth;

    return [startRadius + '%', endRadius + '%'];
};

export const getVoidValue = (
    values: number[],
    maxValue: number = 0
): number => {
    const totalValue = values.reduce((a, b) => a + b, 0);
    return Math.max(totalValue, maxValue) - totalValue;
};

export const toIterableValueSet = (
    valueSetProp: DonutValueSetProp
): DonutValueSet[] => {
    const result: DonutValueSet[] = [];

    if (valueSetProp !== undefined) {
        (Array.isArray(valueSetProp) ? valueSetProp : [valueSetProp]).forEach(valueSet => {
            if (valueSet) {
                result.push(valueSet);
            }
        });
    }

    return result;
};
